import { formatTimeOfDay, isoAttr, formatDateTime } from "../../lib/datetime.ts";
import { useEffect, useId, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import {
  DenseTable,
  StatusBadge,
  defaultNumberFormat,
  type Column,
  type Measure,
} from "../../components/index.ts";
import { DashboardGrid, KpiCard } from "../../components/observability/index.ts";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { useDataQuality } from "../../state/data-quality.tsx";
import { useDialogFocus } from "../../components/index.ts";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import {
  useAssuranceSummary,
  useAssuranceFindings,
  useAssuranceFinding,
  setFilter,
  type AssuranceSummary,
  type AssuranceFindingPage,
  type AssuranceFinding,
  type AssuranceFindingFilters,
  type AssuranceWindow,
} from "./use-assurance.ts";
import {
  quantityMeasure,
  openCriticalHigh,
  isAssuranceEmpty,
  rankAgentsByFindings,
  severityRank,
  severityLabel,
  severityStatus,
  severityTitle,
  severityProminent,
  statusLabel,
  statusStatus,
  engineLabelOrNull,
  findingHref,
  agentFindingsHref,
  SEVERITY_ORDER,
  ENGINE_ORDER,
  STATUS_ORDER,
} from "./vocab.ts";
import { FindingDetail } from "./FindingDetail.tsx";
import type { components } from "@oathera/contract/api";
import "./assurance.css";

type Severity = components["schemas"]["enum_finding_severity"];
type Engine = components["schemas"]["enum_assurance_engine"];
type Status = components["schemas"]["enum_finding_status"];

/**
 * The Code Assurance screen (task 7.4; design §10, requirement 13.4). It composes the regions the
 * requirement calls for over THREE REST reads, all role `viewer`:
 *
 * - `GET /api/v1/assurance/summary` → the headline tiles (open critical/high, block rate, mean time
 *   to fix, scans by surface).
 * - `GET /api/v1/assurance/findings` → the filterable findings table (filter by severity, engine,
 *   agent and status) AND the agents-ranked-by-findings breakdown (derived from the page's items).
 * - `GET /api/v1/assurance/findings/{fingerprint}` → the per-finding decision history, fetched
 *   lazily when a row opens the detail drawer.
 *
 * The summary and findings reads are windowed off the shared time-range, so pausing freezes them
 * (design 12.2, FR-13 AC2). The screen reports the summary's data-quality into the shared strip so
 * the persistent reminder stays consistent (NFR-4), and shows a sampling note in its own meta row.
 *
 * THE GATEWAY_ASSURANCE EMPTY STATE drives the state handling. Assurance is behind the Gateway's
 * `GATEWAY_ASSURANCE` flag (default OFF, requirement 13.4). When the flag is off no scans/findings/
 * decisions ever reach the pipeline, so every zero-filled summary breakdown is zero and the findings
 * list is empty. Rather than show a wall of zero tiles that would read as "all clear", the screen
 * detects that fully-empty shape ({@link isAssuranceEmpty}) and renders an informative empty state
 * explaining the flag. A single real scan or finding flips it to the healthy READY view, so a quiet-
 * but-enabled deployment still renders its real zeros in the tiles and tables.
 *
 * It handles every state a screen must (design 12.3):
 * - restricted: all reads are viewer-level; an unknown/below-viewer role sees the restricted state.
 * - loading / empty / error / ready as the summary + findings reads resolve; a cold-core waking
 *   error defers to the shell's banner (kept "loading" under it so there is no contradiction).
 */
export function AssuranceScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();

  // The server-side findings filters (severity / engine / status / agent). The agent filter is set
  // by clicking an agent in the ranked list; the rest are selects in the toolbar.
  const [filters, setFilters] = useState<AssuranceFindingFilters>({});

  const { query: summary, window } = useAssuranceSummary();
  const { query: findings } = useAssuranceFindings(filters);

  const state = resolveState(role, summary, findings);

  return (
    <ScreenScaffold
      title="Code Assurance"
      subtitle="Assurance scans by verdict and surface, open findings by severity and engine, block rate and mean time to fix, a filterable findings table with per-finding decision history, and agents ranked by findings introduced — behind the Gateway's GATEWAY_ASSURANCE flag."
      state={state}
    >
      {summary.data !== undefined && findings.data !== undefined && (
        <AssuranceBody
          summary={summary.data}
          findings={findings.data}
          window={window}
          live={live}
          filters={filters}
          onFiltersChange={setFilters}
        />
      )}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from the role and the summary + findings query status. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  summary: ReturnType<typeof useAssuranceSummary>["query"],
  findings: ReturnType<typeof useAssuranceFindings>["query"],
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the assurance view is hidden until it is. The server enforces access regardless."
          : "The assurance view needs at least the viewer role.",
    };
  }

  // The screen needs BOTH reads to decide empty-vs-ready honestly (the GATEWAY_ASSURANCE empty state
  // is defined over the summary AND the findings). Both are loaded before READY.
  if (summary.data !== undefined && findings.data !== undefined) {
    if (isAssuranceEmpty(summary.data, findings.data)) {
      return {
        kind: "empty",
        cause:
          "No assurance data in this window. Code Assurance runs behind the Gateway's GATEWAY_ASSURANCE feature flag, which is off by default. When it is enabled, scan verdicts, open findings, block rate, mean time to fix and the findings table appear here. If you expect data, confirm the flag is on for the Gateway and widen the time range.",
      };
    }
    return { kind: "ready" };
  }

  // Surface the first non-waking error from either read; the waking case defers to the shell banner.
  const erroring = summary.isError ? summary : findings.isError ? findings : null;
  if (erroring !== null && erroring.isError) {
    if (erroring.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(erroring.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useAssuranceSummary>["query"]["error"]): string {
  if (error === null) return "The assurance view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see assurance data. The server enforces access.";
    case "network":
      return "The assurance request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function AssuranceBody(props: {
  summary: AssuranceSummary;
  findings: AssuranceFindingPage;
  window: AssuranceWindow;
  live: boolean;
  filters: AssuranceFindingFilters;
  onFiltersChange: (next: AssuranceFindingFilters) => void;
}): ReactNode {
  const { summary, findings } = props;
  const generatedAt = summary.meta.generated_at;
  const sampling = summary.meta.data_quality.sampling_in_effect;

  // The fingerprint of the finding whose detail drawer is open, or null when closed.
  const [selected, setSelected] = useState<string | null>(null);
  const detail = useAssuranceFinding(selected);

  // Keep the persistent data-quality strip consistent with this snapshot (design 12.2, NFR-4), the
  // same way the System/Overview screens do. Only the fields the summary carries are reported.
  const { report } = useDataQuality();
  const dq = summary.meta.data_quality;
  const gapCount = (dq.gaps ?? []).length;
  useEffect(() => {
    report({
      drops: { atSource: 0, atEdge: 0, inPipeline: dq.drops_in_range },
      sampling: { active: dq.sampling_in_effect, rate: null, exact: !dq.sampling_in_effect },
      hasSequenceGap: gapCount > 0,
    });
  }, [report, dq.drops_in_range, dq.sampling_in_effect, gapCount]);

  return (
    <div className="asr" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="asr__meta">
          {props.live ? "Live" : "Paused"} · window{" "}
          <time dateTime={isoAttr(props.window.fromMs)}>{formatTimeOfDay(props.window.fromMs)}</time>{" "}
          to{" "}
          <time dateTime={isoAttr(props.window.toMs)}>{formatTimeOfDay(props.window.toMs)}</time> ·
          generated <time dateTime={isoAttr(generatedAt)}>{formatTimeOfDay(generatedAt)}</time>
          {sampling && (
            <span className="asr__meta-flag" role="note">
              {" "}
              · sampling in effect. Some counts may be sampled
            </span>
          )}
        </p>
      </div>

      {/* Headline tiles: open critical/high, block rate, mean time to fix, scans by surface. */}
      <AssuranceTiles summary={summary} />

      {/* Agents ranked by findings introduced (derived from the current findings page). Clicking an
          agent filters the findings table to that agent. */}
      <AgentsRanked
        findings={findings.items}
        activeAgent={props.filters.agent ?? null}
        onPick={(agent) =>
          props.onFiltersChange(
            setFilter(
              props.filters,
              "agent",
              props.filters.agent === agent ? undefined : agent,
            ),
          )
        }
      />

      {/* The filterable findings table. A row opens the per-finding decision-history drawer. */}
      <FindingsTable
        findings={findings}
        filters={props.filters}
        onFiltersChange={props.onFiltersChange}
        onSelect={setSelected}
      />

      <FindingDrawer
        open={selected !== null}
        fingerprint={selected}
        detail={{ data: detail.data, isError: detail.isError, error: detail.error }}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

/* ---- Tiles ----------------------------------------------------------------------------------- */

/**
 * The four headline tiles (requirement 13.4): open critical/high, block rate, mean time to fix, and
 * total scans by surface (a sum across the always-present surface breakdown). Each tile carries its
 * honest confidence — a value the server could not verify (null) renders the kit's "no reading" empty
 * state rather than a fabricated zero (design 12.3). Presentation-only: every number is derived from
 * the SAME summary the body already holds.
 */
function AssuranceTiles(props: { summary: AssuranceSummary }): ReactNode {
  const { summary } = props;

  const critHigh = openCriticalHigh(summary);
  const blockRate = summary.block_rate;
  const mttf = summary.mean_time_to_fix_s;

  // Total scans across every surface (the surface breakdown is zero-filled over the catalog).
  const scanValues = summary.scans_by_surface.map((r) => r.count);
  let scanTotal = 0;
  let anyScan = false;
  for (const q of scanValues) {
    if (q.value === null) continue;
    scanTotal += q.value;
    anyScan = true;
  }

  return (
    <DashboardGrid
      label="Assurance summary"
      className="asr-tiles"
      kpis={[
        <KpiCard
          key="crit-high"
          label="Open critical / high"
          {...numericTile(critHigh.value, critHigh.measure)}
        />,
        <KpiCard
          key="block-rate"
          label="Block rate"
          {...(blockRate.value === null
            ? emptyTile()
            : {
                value: formatPercent(blockRate.value),
                measure: quantityMeasure(blockRate),
              })}
        />,
        <KpiCard
          key="mttf"
          label="Mean time to fix"
          {...(mttf.value === null
            ? emptyTile()
            : {
                value: formatDuration(mttf.value),
                measure: quantityMeasure(mttf),
              })}
        />,
        <KpiCard
          key="scans"
          label="Scans in window"
          {...(anyScan
            ? { value: defaultNumberFormat(scanTotal) }
            : emptyTile("No scans in this window."))}
        />,
      ]}
    />
  );
}

/** A numeric tile from a summed value + measure, or the honest empty state when the value is null. */
function numericTile(
  value: number | null,
  measure: Measure,
): { value: string; empty?: boolean; emptyHint?: string; measure?: Measure } {
  if (value === null) return emptyTile();
  return { value: defaultNumberFormat(value), measure };
}

/** The kit's honest "no reading" tile props. */
function emptyTile(hint = "No reading in this window."): {
  value: string;
  empty: boolean;
  emptyHint: string;
} {
  return { value: "n/a", empty: true, emptyHint: hint };
}

/** Format a 0..1 block rate as a percentage; a rate already in 0..100 is shown as-is. */
function formatPercent(value: number): string {
  const pct = value <= 1 ? value * 100 : value;
  return `${pct.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

/** Format a mean-time-to-fix in seconds as a human duration (s / m / h / d). */
function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "n/a";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toLocaleString(undefined, { maximumFractionDigits: 1 })}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toLocaleString(undefined, { maximumFractionDigits: 1 })}h`;
  const days = hours / 24;
  return `${days.toLocaleString(undefined, { maximumFractionDigits: 1 })}d`;
}

/* ---- Agents ranked --------------------------------------------------------------------------- */

/**
 * Agents ranked by findings introduced (requirement 13.4), derived from the current findings page.
 * Each row shows the agent, its finding count and worst severity (icon+text). A row is clickable: it
 * filters the findings table to that agent (and clicking the active agent clears the filter), so the
 * ranked list and the table stay linked. An agent with no attributed findings cannot appear (an
 * unattributed finding is honestly excluded, not bucketed under a fabricated row).
 */
function AgentsRanked(props: {
  findings: readonly AssuranceFinding[];
  activeAgent: string | null;
  onPick: (agent: string) => void;
}): ReactNode {
  const navigate = useNavigate();
  const rows = rankAgentsByFindings(props.findings);

  const columns: readonly Column<(typeof rows)[number]>[] = [
    {
      key: "agent",
      header: "Agent",
      isRowHeader: true,
      cell: (r) => <code className="asr-mono">{r.agent}</code>,
    },
    {
      key: "worst",
      header: "Worst severity",
      cell: (r) => (
        <span
          className="asr-sev-cell"
          data-prominent={severityProminent(r.worstSeverity) ? "true" : undefined}
        >
          <StatusBadge
            level={severityStatus(r.worstSeverity)}
            label={severityLabel(r.worstSeverity)}
            title={severityTitle(r.worstSeverity)}
            size="sm"
          />
        </span>
      ),
    },
    {
      key: "count",
      header: "Findings",
      align: "right",
      cell: (r) => <span className="asr-num asr-num--strong">{defaultNumberFormat(r.count)}</span>,
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      cell: (r) => (
        <button
          type="button"
          className="asr-link"
          onClick={() => navigate(agentFindingsHref(r.agent))}
          aria-label={`Open the finding events introduced by agent ${r.agent}`}
        >
          Events
        </button>
      ),
    },
  ];

  return (
    <section className="asr-section" aria-label="Agents ranked by findings">
      <div className="asr-section__head">
        <h2 className="asr-section__title">Agents by findings introduced</h2>
        <p className="asr-section__note">
          Agents in this window, most findings first. Select an agent to filter the findings below.
        </p>
      </div>
      <DenseTable
        caption="Agents ranked by the number of assurance findings they introduced, with worst severity"
        captionHidden
        columns={columns}
        rows={rows}
        rowKey={(r) => r.agent}
        onRowClick={(r) => props.onPick(r.agent)}
        rowLabel={(r) =>
          `${props.activeAgent === r.agent ? "Clear filter for" : "Filter findings by"} agent ${r.agent} (${r.count} findings)`
        }
        highlightRowKey={props.activeAgent}
        stickyHeader
        emptyMessage="No agent-attributed findings in this window. Findings with no agent are not ranked here."
      />
    </section>
  );
}

/* ---- Findings table (filterable) ------------------------------------------------------------- */

/**
 * The filterable findings table (requirement 13.4: filter by severity, engine, agent, status). The
 * severity/engine/status filters are selects in the toolbar; the agent filter is driven by the ranked
 * list above and shown here as a clearable chip. The counts-by-severity and counts-by-engine the page
 * carries are summarised in the toolbar so the filters are informed. Rows are sorted by severity
 * (most severe first) then most-recently-seen; a row opens the per-finding decision-history drawer.
 */
function FindingsTable(props: {
  findings: AssuranceFindingPage;
  filters: AssuranceFindingFilters;
  onFiltersChange: (next: AssuranceFindingFilters) => void;
  onSelect: (fingerprint: string) => void;
}): ReactNode {
  const { findings, filters } = props;
  const navigate = useNavigate();

  const rows = [...findings.items].sort(
    (a, b) => severityRank(b.severity) - severityRank(a.severity) || b.last_seen - a.last_seen,
  );

  const columns: readonly Column<AssuranceFinding>[] = [
    {
      key: "severity",
      header: "Severity",
      isRowHeader: true,
      cell: (f) => (
        <span
          className="asr-sev-cell"
          data-prominent={severityProminent(f.severity) ? "true" : undefined}
        >
          <StatusBadge
            level={severityStatus(f.severity)}
            label={severityLabel(f.severity)}
            title={severityTitle(f.severity)}
            size="sm"
          />
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (f) => (
        <StatusBadge level={statusStatus(f.status)} label={statusLabel(f.status)} size="sm" />
      ),
    },
    {
      key: "engine",
      header: "Engine",
      cell: (f) => <span className="asr-faint">{engineLabelOrNull(f.engine)}</span>,
    },
    {
      key: "rule",
      header: "Rule",
      cell: (f) =>
        f.rule_id === null || f.rule_id === undefined ? (
          <span className="asr-faint">—</span>
        ) : (
          <code className="asr-mono">{f.rule_id}</code>
        ),
    },
    {
      key: "agent",
      header: "Agent",
      cell: (f) =>
        f.agent_sub === null || f.agent_sub === undefined ? (
          <span className="asr-faint">No agent</span>
        ) : (
          <code className="asr-mono">{f.agent_sub}</code>
        ),
    },
    {
      key: "last_seen",
      header: "Last seen",
      align: "right",
      cell: (f) => (
        <time className="asr-num" dateTime={isoAttr(f.last_seen)}>
          {formatDateTime(f.last_seen)}
        </time>
      ),
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      cell: (f) => (
        <button
          type="button"
          className="asr-link"
          onClick={() => navigate(findingHref(f))}
          aria-label={`Open the events for the ${severityLabel(f.severity).toLowerCase()} finding ${f.fingerprint}`}
        >
          Events
        </button>
      ),
    },
  ];

  return (
    <section className="asr-section" aria-label="Findings">
      <div className="asr-section__head">
        <h2 className="asr-section__title">Findings</h2>
        <p className="asr-section__note">
          Open findings deduped on fingerprint, most severe first. Select a row for its decision
          history.
        </p>
      </div>

      <FindingFilters filters={filters} onChange={props.onFiltersChange} />

      <DenseTable
        caption="Assurance findings with severity, status, engine, rule, agent and last-seen time"
        captionHidden
        columns={columns}
        rows={rows}
        rowKey={(f) => f.fingerprint}
        onRowClick={(f) => props.onSelect(f.fingerprint)}
        rowLabel={(f) =>
          `View the decision history for the ${severityLabel(f.severity).toLowerCase()} ${statusLabel(f.status).toLowerCase()} finding ${f.fingerprint}`
        }
        stickyHeader
        emptyMessage="No findings match the current filters in this window. Clear a filter or widen the time range."
      />
    </section>
  );
}

/** The severity / engine / status selects and the agent-filter chip. */
function FindingFilters(props: {
  filters: AssuranceFindingFilters;
  onChange: (next: AssuranceFindingFilters) => void;
}): ReactNode {
  const { filters } = props;
  const sevId = useId();
  const engId = useId();
  const statusId = useId();

  return (
    <div className="asr-filters" role="group" aria-label="Filter findings">
      <label className="asr-filter" htmlFor={sevId}>
        <span className="asr-filter__label">Severity</span>
        <select
          id={sevId}
          className="asr-filter__select"
          value={filters.severity ?? ""}
          onChange={(e) =>
            props.onChange(
              setFilter(
                filters,
                "severity",
                e.target.value === "" ? undefined : (e.target.value as Severity),
              ),
            )
          }
        >
          <option value="">All severities</option>
          {SEVERITY_ORDER.map((s) => (
            <option key={s} value={s}>
              {severityLabel(s)}
            </option>
          ))}
        </select>
      </label>

      <label className="asr-filter" htmlFor={engId}>
        <span className="asr-filter__label">Engine</span>
        <select
          id={engId}
          className="asr-filter__select"
          value={filters.engine ?? ""}
          onChange={(e) =>
            props.onChange(
              setFilter(
                filters,
                "engine",
                e.target.value === "" ? undefined : (e.target.value as Engine),
              ),
            )
          }
        >
          <option value="">All engines</option>
          {ENGINE_ORDER.map((eng) => (
            <option key={eng} value={eng}>
              {engineLabelOrNull(eng)}
            </option>
          ))}
        </select>
      </label>

      <label className="asr-filter" htmlFor={statusId}>
        <span className="asr-filter__label">Status</span>
        <select
          id={statusId}
          className="asr-filter__select"
          value={filters.status ?? ""}
          onChange={(e) =>
            props.onChange(
              setFilter(
                filters,
                "status",
                e.target.value === "" ? undefined : (e.target.value as Status),
              ),
            )
          }
        >
          <option value="">All statuses</option>
          {STATUS_ORDER.map((st) => (
            <option key={st} value={st}>
              {statusLabel(st)}
            </option>
          ))}
        </select>
      </label>

      {filters.agent !== undefined && (
        <button
          type="button"
          className="asr-chip"
          onClick={() => props.onChange(setFilter(filters, "agent", undefined))}
          aria-label={`Clear the agent filter for ${filters.agent}`}
        >
          <span className="asr-chip__label">Agent</span>
          <code className="asr-mono">{filters.agent}</code>
          <span className="asr-chip__x" aria-hidden="true">
            ✕
          </span>
        </button>
      )}
    </div>
  );
}

/* ---- Detail drawer --------------------------------------------------------------------------- */

/**
 * A right-side sliding detail panel for the selected finding (self-contained, modelled on the shared
 * dialog pattern). It is an accessible modal dialog — `useDialogFocus` moves focus in, traps Tab,
 * closes on Escape and returns focus to the trigger (design 12.4) — and the backdrop click closes it.
 * Rendered only when open, so there is no inert dialog in the tree when nothing is selected.
 */
function FindingDrawer(props: {
  open: boolean;
  fingerprint: string | null;
  detail: FindingDetailQuery;
  onClose: () => void;
}): ReactNode {
  const panelRef = useDialogFocus<HTMLDivElement>(props.onClose);
  const titleId = useId();
  if (!props.open || props.fingerprint === null) return null;
  return (
    <div className="asr-drawer-root">
      <div className="asr-drawer-backdrop" onClick={props.onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="asr-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <FindingDetail
          fingerprint={props.fingerprint}
          detail={props.detail}
          onClose={props.onClose}
          titleId={titleId}
        />
      </div>
    </div>
  );
}

/** The detail-query shape the drawer passes through to {@link FindingDetail}. */
type FindingDetailQuery = {
  readonly data: ReturnType<typeof useAssuranceFinding>["data"];
  readonly isError: boolean;
  readonly error: ReturnType<typeof useAssuranceFinding>["error"];
};
